export type SceneBackgroundImageFit = 'cover' | 'contain' | 'stretch'

export interface SceneBackgroundUvTransformInput {
  fit: SceneBackgroundImageFit
  imageWidth: number
  imageHeight: number
  viewportWidth: number
  viewportHeight: number
  zoom: number
  offsetX: number
  offsetY: number
  rotationDegrees: number
}

export interface SceneBackgroundUvTransform {
  repeatX: number
  repeatY: number
  offsetX: number
  offsetY: number
  rotationRadians: number
}

function normalizePositive(value: number, fallback: number): number {
  return Number.isFinite(value) && value > 0 ? value : fallback
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Number.isFinite(value) ? value : min))
}

/**
 * 计算 Three.js background Texture 使用的无拉伸 UV 变换。
 *
 * contain 模式由调用方先把原图居中绘制到与视口同宽高比的 CanvasTexture，因此其基础
 * UV 与 stretch 相同；之后仍可叠加缩放、焦点偏移和旋转。
 */
export function computeSceneBackgroundUvTransform(
  input: SceneBackgroundUvTransformInput,
): SceneBackgroundUvTransform {
  const imageWidth = normalizePositive(input.imageWidth, 1)
  const imageHeight = normalizePositive(input.imageHeight, 1)
  const viewportWidth = normalizePositive(input.viewportWidth, 1)
  const viewportHeight = normalizePositive(input.viewportHeight, 1)
  const imageAspect = imageWidth / imageHeight
  const viewportAspect = viewportWidth / viewportHeight

  let baseRepeatX = 1
  let baseRepeatY = 1
  if (input.fit === 'cover') {
    if (imageAspect > viewportAspect) {
      baseRepeatX = viewportAspect / imageAspect
    } else if (imageAspect < viewportAspect) {
      baseRepeatY = imageAspect / viewportAspect
    }
  }

  const zoom = normalizePositive(input.zoom, 1)
  const rotationRadians = (clamp(input.rotationDegrees, -180, 180) * Math.PI) / 180
  const rotationExtent = Math.abs(Math.cos(rotationRadians)) + Math.abs(Math.sin(rotationRadians))
  const rotationCropScale = Math.min(
    1,
    1 / (baseRepeatX * rotationExtent),
    1 / (baseRepeatY * rotationExtent),
  )
  const repeatX = (baseRepeatX * rotationCropScale) / zoom
  const repeatY = (baseRepeatY * rotationCropScale) / zoom
  const sampledExtentX = Math.min(1, repeatX * rotationExtent)
  const sampledExtentY = Math.min(1, repeatY * rotationExtent)
  const offsetX = clamp(input.offsetX, -1, 1) * ((1 - sampledExtentX) / 2)
  const offsetY = clamp(input.offsetY, -1, 1) * ((1 - sampledExtentY) / 2)

  return {
    repeatX,
    repeatY,
    offsetX,
    offsetY,
    rotationRadians,
  }
}
